import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { getColorsForCategories } from './colors';

export default async function fetchPercentageNoHealthInsuranceSupabase(location_name) {
  try {
    
    // Initialize Supabase location service to get county information
    await supabaseLocationService.initialize();
    
    // Check if this is a city and get its county
    const locationInfo = supabaseLocationService.getAvailableLocations().find(loc => loc.name === location_name);
    const isCity = locationInfo && locationInfo.type === 'city';
    const countyName = isCity ? locationInfo.county : null;
    
    
                // Build targeted query for only the locations we need
            
            // Use proper server-side filtering to only download the data we need
            
            // Create separate queries for each location (like the working HTML version)
            let localQuery = supabaseClient
              .from('percent_no_health_insure')
              .select('*')
              .eq('location_name', location_name)
              .gte('year', 2015)
              .lte('year', 2023)
              .order('year', { ascending: true });
            
            let californiaQuery = supabaseClient
              .from('percent_no_health_insure')
              .select('*')
              .eq('location_name', 'California')
              .gte('year', 2015)
              .lte('year', 2023)
              .order('year', { ascending: true });
            
            let countyQuery = null;
            if (isCity && countyName) {
              // Try both with and without "County" suffix
              const countyWithoutSuffix = countyName.replace(/\s+County$/i, '');
              countyQuery = supabaseClient
                .from('percent_no_health_insure')
                .select('*')
                .or(`location_name.eq.${countyName},location_name.eq.${countyWithoutSuffix}`)
                .gte('year', 2015)
                .lte('year', 2023)
                .order('year', { ascending: true });
            }
            
            // Execute queries separately
            const { data: localQueryData, error: localError } = await localQuery;
            
            const { data: californiaQueryData, error: californiaError } = await californiaQuery;
            
            let countyQueryData = [];
            if (countyQuery) {
              const { data: countyResult, error: countyError } = await countyQuery;
              if (!countyError && countyResult) {
                countyQueryData = countyResult;
              }
            }
            
            if (localError) throw localError;
            if (californiaError) throw californiaError;
            
            // Combine all data
            const data = [...(localQueryData || []), ...(californiaQueryData || []), ...countyQueryData];
    
            
                // Separate local vs California vs county data
            // Since we're querying for specific locations, filtering should be simpler
            const localData = data.filter(d => 
              d.location_name && 
              d.location_name.toString().toLowerCase().includes(location_name.toLowerCase())
            );
            const californiaData = data.filter(d => 
              d.location_name && 
              d.location_name.toString().toLowerCase().includes('california')
            );
            const countyData = isCity && countyName ? data.filter(d => {
              if (!d.location_name) return false;
              const locationLower = d.location_name.toString().toLowerCase();
              const countyLower = countyName.toLowerCase();
              const locationNameLower = location_name.toLowerCase();
              
              // Check if this is county data (contains county name but not city name)
              const isCounty = locationLower.includes(countyLower) && !locationLower.includes(locationNameLower);
              
              // Also check for variations like "Yolo" vs "Yolo County"
              const countyWithoutSuffix = countyLower.replace(/\s+county$/i, '');
              const isCountyVariation = locationLower.includes(countyWithoutSuffix) && !locationLower.includes(locationNameLower);
              
              return isCounty || isCountyVariation;
            }) : [];
            
            // Debug: Show what we're searching for vs what we found
            if (isCity && countyName) {
            }
            
            // Debug: Show what years are available for each location
            if (isCity && countyData.length > 0) {
            }
    
    if (localData.length === 0 || californiaData.length === 0) {
      throw new Error('Insufficient data for chart generation');
    }
    
                const localName = localData[0]?.location_name || location_name;
            let years = [...new Set(data.map(row => row.year))].sort((a, b) => a - b);
            
            
            // Log the year range we found

                const getSeries = (groupName) => years.map(year => {
              const row = data.find(r => 
                r.year === year && 
                r.location_name && 
                r.location_name.toString().toLowerCase().includes(groupName.toLowerCase())
              );
              return row && row.percent_no_insurance ? Number(row.percent_no_insurance) : null;
            });


    
    // Build labels and get colors
    let labels = [localName, 'California'];
    if (isCity && countyData.length > 0) {
      labels.push(`${countyName} County`);
    }
    
    const palette = getColorsForCategories(labels);

    // Calculate indicators
    const latestYear = years[years.length - 1];
    const latestLocal = getSeries(localName)[getSeries(localName).length - 1] || 0;
    const latestCA = getSeries('California')[getSeries('California').length - 1] || 0;
    const deltaPoints = latestLocal - latestCA; // percentage points difference
    const firstLocal = getSeries(localName)[0] || 0;
    const changePointsSinceStart = latestLocal - firstLocal;
    const trend = changePointsSinceStart > 0 ? 'increased' : changePointsSinceStart < 0 ? 'decreased' : 'stayed the same';

    // Build narrative with county information if available
    let smartNarrative = `In ${location_name}, ${latestLocal.toFixed(1)}% of residents lack health insurance coverage in ${latestYear}, which has ${trend} by ${Math.abs(changePointsSinceStart).toFixed(1)} percentage points since the start of the series. Compared with California (${latestCA.toFixed(1)}%), the difference is ${deltaPoints >= 0 ? '+' : ''}${deltaPoints.toFixed(1)} points.`;
    
    // Add county comparison to narrative if available
    if (isCity && countyData.length > 0) {
      const latestCounty = countyData.find(r => r.year === latestYear);
      if (latestCounty && latestCounty.percent_no_insurance !== undefined) {
        const countyValue = Number(latestCounty.percent_no_insurance);
        const countyDelta = latestLocal - countyValue;
        smartNarrative += ` Compared to ${countyName} County (${countyValue.toFixed(1)}%), ${location_name} is ${countyDelta >= 0 ? 'higher' : 'lower'} by ${Math.abs(countyDelta).toFixed(1)} percentage points.`;
      }
    }

    // Build datasets array
    const datasets = [
      {
        label: `${localName}`,
        data: getSeries(localName),
        borderColor: palette[0],
        backgroundColor: palette[0],
        borderWidth: 2,
        tension: 0.3,
        fill: false
      },
      {
        label: 'California',
        data: getSeries('California'),
        borderColor: palette[1],
        backgroundColor: palette[1],
        borderWidth: 2,
        tension: 0.3,
        fill: false
      }
    ];

    // Add county dataset if available
    if (isCity && countyData.length > 0) {
      const countySeries = years.map(year => {
        const row = countyData.find(r => r.year === year);
        return row && row.percent_no_insurance ? Number(row.percent_no_insurance) : null;
      });
      
      datasets.push({
        label: `${countyName} County`,
        data: countySeries,
        borderColor: palette[2],
        backgroundColor: palette[2],
        borderWidth: 2,
        tension: 0.3,
        fill: false
      });
    }

    
    return {
      chartData: {
        labels: years,
        datasets: datasets
      },
      chartOptions: {
        type: 'line',
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            bottom: 20,
            top: 10,
            left: 10,
            right: 10
          }
        },
        plugins: {
          legend: standardLegend,
          title: standardTitle(
            isCity && countyData.length > 0
              ? `Health Insurance Coverage - ${location_name} vs ${countyName} County vs California (${years[0]}–${years[years.length - 1]})`
              : `Health Insurance Coverage - ${location_name} (${years[0]}–${years[years.length - 1]})`
          ),
          tooltip: {
            callbacks: {
              label: context => `${context.dataset.label}: ${typeof context.parsed.y === 'number' ? context.parsed.y.toFixed(1) : 'N/A'}%`
            }
          }
        },
        scales: {
          y: {
            title: {
              display: true,
              text: '% Without Insurance'
            },
            ticks: {
              callback: value => `${Number(value).toFixed(1)}%`
            },
            beginAtZero: true
          },
          x: {
            title: {
              display: true,
              text: 'Year'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45
            }
          }
        }
      },
      narrative: smartNarrative,
      countyData: isCity && countyData.length > 0 ? {
        name: countyName,
        latestValue: countyData.find(r => r.year === latestYear)?.percent_no_insurance,
        data: countyData
      } : null
    };

  } catch (err) {
    console.error('Failed to generate health insurance chart:', err);
    throw err;
  }
}
